/**
 * 🎯 Campaign Slot Editor
 * Editor für einzelne Slots in Multi-Campaigns mit allen Konfigurationsoptionen
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronDown, ChevronUp, X, Plus, Trash2, Upload, 
  Mail, Users, Server, Settings,
  AlertCircle, Eye, EyeOff
} from 'lucide-react';
import { useMutation } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';

const CampaignSlotEditor = ({ 
  slot, 
  slotNumber, 
  centralRecipients, 
  domainAnalysis, 
  smtpServers,
  globalAttachments = [],
  onUpdate, 
  onRemove 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recipientPreview, setRecipientPreview] = useState([]);
  const [domainFilterText, setDomainFilterText] = useState('');

  // Empfänger-Segmentierung Mutation
  const segmentRecipientsMutation = useMutation(
    async ({ recipients, domainFilters, maxRecipients }) => {
      const response = await axios.post('/api/domain-segmentation/segment', {
        recipients,
        domain_filters: domainFilters,
        max_recipients: maxRecipients
      });
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        setRecipientPreview(data.recipients.slice(0, 10)); // Nur erste 10 für Preview
        toast.success(`${data.count} Empfänger für Slot gefiltert`);
      },
      onError: (error) => {
        toast.error('Empfänger-Segmentierung fehlgeschlagen');
        console.error('Segmentation error:', error);
      }
    }
  );

  // Domain-Filter aus Text parsen
  const parseDomainFilters = (text) => {
    if (!text) return [];
    return text.split(/[,\n]/)
      .map(domain => domain.trim().toLowerCase())
      .filter(domain => domain && domain.includes('.'));
  };

  // Domain-Filter Text ändern
  const handleDomainFilterChange = (text) => {
    setDomainFilterText(text);
    const filters = parseDomainFilters(text);
    onUpdate({ domain_filters: filters });

    // Auto-Segmentierung bei zentraler Empfängerliste
    if (slot.recipient_source === 'domain_segmentation' && centralRecipients.length > 0 && filters.length > 0) {
      segmentRecipientsMutation.mutate({
        recipients: centralRecipients,
        domainFilters: filters,
        maxRecipients: slot.max_recipients
      });
    }
  };

  // Template hinzufügen
  const addTemplate = () => {
    if (!slot.html_templates) {
      onUpdate({ html_templates: [''] });
      return;
    }
    const newTemplates = [...slot.html_templates, ''];
    onUpdate({ html_templates: newTemplates });
  };

  // Template entfernen
  const removeTemplate = (index) => {
    if (!slot.html_templates) return;  // ← Schutz vor null
    const newTemplates = slot.html_templates.filter((_, i) => i !== index);
    onUpdate({ html_templates: newTemplates });
  };

  // Template aktualisieren
  const updateTemplate = (index, value) => {
    if (!slot.html_templates) return;
    const newTemplates = [...slot.html_templates];
    newTemplates[index] = value;
    onUpdate({ html_templates: newTemplates });
  };

  // Gewichtung aktualisieren
  const updateTemplateWeight = (templateIndex, weight) => {
    const newWeights = { ...slot.template_weights };
    newWeights[`template_${templateIndex}`] = parseFloat(weight) / 100;
    onUpdate({ template_weights: newWeights });
  };

  // SMTP-Server Toggle
  const toggleSmtpServer = (serverId) => {
    const currentIds = slot.smtp_server_ids || [];
    const newIds = currentIds.includes(serverId)
      ? currentIds.filter(id => id !== serverId)
      : [...currentIds, serverId];
    onUpdate({ smtp_server_ids: newIds });
  };

  // Empfänger-Quelle ändern
  const handleRecipientSourceChange = useCallback((source) => {
    onUpdate({ 
      recipient_source: source,
      recipients: source === 'own_list' ? slot.recipients : [],
      domain_filters: source === 'domain_segmentation' ? slot.domain_filters : []
    });
  }, [onUpdate, slot.recipients, slot.domain_filters]);

  // Eigene Empfänger parsen
  const parseOwnRecipients = (text) => {
    if (!text) return [];
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const matches = text.match(emailRegex) || [];
    return matches; // ✅ Duplikate NICHT entfernen - alle E-Mails beibehalten
  };

  // Slot-spezifische Anhänge hochladen
  const handleSlotAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // File size check
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Datei ${file.name} ist zu groß (max. 10MB)`);
          continue;
        }

        // Create FormData for upload
        const formData = new FormData();
        formData.append('file', file);

        // Upload file
        const response = await axios.post('/api/attachments/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data.success) {
          uploadedFiles.push({
            id: response.data.data.id,
            name: file.name,
            size: file.size,
            type: file.type,
            path: response.data.data.path
          });
        }
      }

      // Add to slot attachments
      const currentAttachments = slot.attachments || [];
      onUpdate({ attachments: [...currentAttachments, ...uploadedFiles] });

      toast.success(`${uploadedFiles.length} Datei(en) erfolgreich hochgeladen`);
      
    } catch (error) {
      toast.error('Fehler beim Hochladen der Dateien');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // Domain-Filter aus Analyse übernehmen
  const applyDomainSuggestion = (domains) => {
    const domainText = domains.join(', ');
    setDomainFilterText(domainText);
    handleDomainFilterChange(domainText);
  };

  // Initialisierung
  useEffect(() => {
    if (slot.domain_filters && slot.domain_filters.length > 0) {
      setDomainFilterText(slot.domain_filters.join(', '));
    }
  }, [slot.domain_filters]);

  // Automatisch auf own_list umstellen wenn keine zentrale Liste vorhanden
  useEffect(() => {
    if (!centralRecipients || centralRecipients.length === 0) {
      if (slot.recipient_source !== 'own_list') {
        handleRecipientSourceChange('own_list');
      }
    }
  }, [centralRecipients, slot.recipient_source, handleRecipientSourceChange]);

  // Geschätzte Empfänger-Anzahl berechnen
  const getEstimatedRecipientCount = () => {
    if (slot.recipient_source === 'own_list') {
      return slot.recipients.length;
    } else if (slot.recipient_source === 'central_portion') {
      // Anteil der zentralen Liste
      return slot.max_recipients || 0;
    } else if (slot.recipient_source === 'domain_segmentation') {
      if (recipientPreview.length > 0) {
        return segmentRecipientsMutation.data?.count || 0;
      }
      // Schätzung basierend auf Domain-Analyse
      if (domainAnalysis && slot.domain_filters.length > 0) {
        let estimated = 0;
        slot.domain_filters.forEach(filter => {
          const domain = domainAnalysis.domains[filter];
          if (domain) estimated += domain;
        });
        return Math.min(estimated, slot.max_recipients || estimated);
      }
    }
    return 0;
  };

  const estimatedRecipients = getEstimatedRecipientCount();

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-blue-600">{slotNumber}</span>
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{slot.name}</h3>
            <div className="text-sm text-gray-500 flex items-center space-x-4">
              <span>~{estimatedRecipients} Empfänger</span>
              {slot.html_templates ? (
              <span>{slot.html_templates.filter(t => t.trim()).length} Templates</span>
              ) : slot.ai_content_variant_group ? (
                <span>🤖 AI ({slot.ai_content_variant_group.toUpperCase()})</span>
              ) : (
                <span>0 Templates</span>
              )}
              <span>{slot.emails_per_hour}/h</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="text-red-500 hover:text-red-700 p-1"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Slot-Grundlagen */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Slot-Name
              </label>
              <input
                type="text"
                value={slot.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                E-Mails pro Stunde
              </label>
              <input
                type="number"
                value={slot.emails_per_hour}
                onChange={(e) => onUpdate({ emails_per_hour: parseInt(e.target.value) || 100 })}
                min="1"
                max="10000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Beschreibung
            </label>
            <input
              type="text"
              value={slot.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Kurze Beschreibung des Slots..."
            />
          </div>

          {/* Empfänger-Konfiguration */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Empfänger-Konfiguration
            </h4>

            {/* Zentrale Liste vorhanden - Anteil-Modus */}
            {centralRecipients && centralRecipients.length > 0 ? (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-3">
                  📊 Zentrale Empfängerliste ({centralRecipients.length} Empfänger)
                </div>
                <div className="text-sm text-blue-700 mb-4">
                  Dieser Slot erhält einen Anteil der zentralen Liste. Wählen Sie die Anzahl der Empfänger für diesen Slot:
                </div>
                
                {/* Empfänger-Anteil Eingabe */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Anzahl Empfänger für diesen Slot
                    </label>
                    <input
                      type="number"
                      value={slot.max_recipients || ''}
                      onChange={(e) => onUpdate({ 
                        max_recipients: e.target.value ? parseInt(e.target.value) : null,
                        recipient_source: 'central_portion' 
                      })}
                      min="1"
                      max={centralRecipients.length}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={`Max. ${centralRecipients.length}`}
                    />
                  </div>

                  {/* Prozent-Buttons */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Schnellauswahl (Prozent):</div>
                    <div className="flex flex-wrap gap-2">
                      {[10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100].map(percent => {
                        const count = Math.floor(centralRecipients.length * (percent / 100));
                        return (
                          <button
                            key={percent}
                            onClick={() => onUpdate({ 
                              max_recipients: count,
                              recipient_source: 'central_portion'
                            })}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                          >
                            {percent}% ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fortschritts-Anzeige */}
                  {slot.max_recipients && (
                    <div className="bg-white p-3 rounded border">
                      <div className="text-sm">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">Slot-Anteil:</span>
                          <span className="text-blue-600 font-medium">
                            {slot.max_recipients} von {centralRecipients.length} Empfängern
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-gray-600">
                          <span>Prozent:</span>
                          <span>{Math.round((slot.max_recipients / centralRecipients.length) * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Keine zentrale Liste - Nur eigene Liste möglich */
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-900 mb-2">
                  📝 Eigene Empfängerliste
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  Da keine zentrale Empfängerliste definiert ist, muss jeder Slot seine eigenen Empfänger haben.
                </div>
              </div>
            )}

            {/* Eigene Empfängerliste */}
            {slot.recipient_source === 'own_list' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Empfänger-E-Mails
                </label>
                <textarea
                  value={slot.recipients.join('\n')}
                  onChange={(e) => onUpdate({ recipients: parseOwnRecipients(e.target.value) })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="E-Mail-Adressen eingeben (eine pro Zeile)..."
                />
                <div className="mt-2 text-sm text-gray-500">
                  {slot.recipients.length} Empfänger
                </div>
              </div>
            )}

            {/* Domain-Segmentierung (nur wenn zentrale Liste vorhanden) */}
            {slot.recipient_source === 'domain_segmentation' && centralRecipients && centralRecipients.length > 0 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Domain-Filter
                  </label>
                  <textarea
                    value={domainFilterText}
                    onChange={(e) => handleDomainFilterChange(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Domains eingeben (z.B. gmail.com, outlook.com)..."
                  />
                  <div className="mt-2 text-sm text-gray-500">
                    {slot.domain_filters.length} Domain-Filter aktiv
                  </div>
                </div>

                {/* Domain-Vorschläge aus Analyse */}
                {domainAnalysis && domainAnalysis.suggestions.length > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-blue-900 mb-2">Domain-Vorschläge:</div>
                    <div className="flex flex-wrap gap-2">
                      {domainAnalysis.suggestions.slice(0, 6).map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => applyDomainSuggestion(suggestion.domains)}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          {suggestion.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Max Empfänger */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Maximale Empfänger (optional)
                  </label>
                  <input
                    type="number"
                    value={slot.max_recipients || ''}
                    onChange={(e) => onUpdate({ max_recipients: e.target.value ? parseInt(e.target.value) : null })}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Unbegrenzt"
                  />
                </div>

                {/* Empfänger-Preview */}
                {recipientPreview.length > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm font-medium text-green-900 mb-2">
                      Empfänger-Preview ({segmentRecipientsMutation.data?.count || 0} gesamt):
                    </div>
                    <div className="text-xs text-green-700 space-y-1">
                      {recipientPreview.map((email, index) => (
                        <div key={index}>{email}</div>
                      ))}
                      {segmentRecipientsMutation.data?.count > 10 && (
                        <div className="font-medium">
                          +{segmentRecipientsMutation.data.count - 10} weitere...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* E-Mail-Konfiguration */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center">
              <Mail className="h-4 w-4 mr-2" />
              E-Mail-Konfiguration
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Betreff *
                </label>
                <input
                  type="text"
                  value={slot.subject}
                  onChange={(e) => onUpdate({ subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="E-Mail Betreff..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Absender-Name
                </label>
                <input
                  type="text"
                  value={slot.from_name}
                  onChange={(e) => onUpdate({ from_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Max Mustermann"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Antwort-E-Mail
              </label>
              <input
                type="email"
                value={slot.reply_to_email}
                onChange={(e) => onUpdate({ reply_to_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="reply@example.com"
              />
            </div>
          </div>

          {/* HTML-Templates */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900 flex items-center">
                <Settings className="h-4 w-4 mr-2" />
                {slot.ai_content_variant_group ? (
                  <>🤖 AI-Content ({slot.ai_content_variant_group.toUpperCase()})</>
                ) : (
                  <>HTML-Templates ({slot.html_templates ? slot.html_templates.filter(t => t.trim()).length : 0})</>
                )}
              </h4>
              {!slot.ai_content_variant_group && (
              <button
                onClick={addTemplate}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 flex items-center space-x-1"
              >
                <Plus className="h-3 w-3" />
                <span>Template</span>
              </button>
              )}
            </div>

            {/* Template-Rotation Modus */}
            {slot.html_templates && slot.html_templates.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Template-Rotation
                </label>
                <select
                  value={slot.template_rotation_mode}
                  onChange={(e) => onUpdate({ template_rotation_mode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="equal_distribution">Gleichmäßige Verteilung</option>
                  <option value="random">Zufällige Rotation</option>
                  <option value="sequential">Sequenzielle Rotation</option>
                  <option value="weighted">Gewichtete Verteilung</option>
                </select>
              </div>
            )}

            {/* AI-Content Info oder Templates */}
            {slot.ai_content_variant_group ? (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                <div className="text-sm text-gray-700 dark:text-gray-200">
                  <div className="font-medium mb-2">🤖 AI-Generierter Content</div>
                  <div className="space-y-1 text-xs">
                    <div>📝 Variant-Group: <strong>{slot.ai_content_variant_group.toUpperCase()}</strong></div>
                    <div>🎯 Batch-Index: <strong>{slot.ai_batch_offset}</strong></div>
                    <div>💡 Der Content wird zur Laufzeit aus der AI-Tabelle geladen</div>
                  </div>
                </div>
              </div>
            ) : (
            <div className="space-y-4">
                {slot.html_templates && slot.html_templates.map((template, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium">Template {index + 1}</h5>
                    <div className="flex items-center space-x-2">
                      {slot.template_rotation_mode === 'weighted' && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Gewichtung:</span>
                          <input
                            type="number"
                            value={Math.round((slot.template_weights[`template_${index}`] || (1 / slot.html_templates.length)) * 100)}
                            onChange={(e) => updateTemplateWeight(index, e.target.value)}
                            min="1"
                            max="100"
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <span className="text-sm text-gray-600">%</span>
                        </div>
                      )}
                      {slot.html_templates && slot.html_templates.length > 1 && (
                        <button
                          onClick={() => removeTemplate(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <textarea
                    value={template}
                    onChange={(e) => updateTemplate(index, e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="HTML-Template eingeben..."
                  />
                </div>
              ))}
            </div>
            )}
          </div>

          {/* SMTP-Server Auswahl */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center">
              <Server className="h-4 w-4 mr-2" />
              SMTP-Server Auswahl
            </h4>

            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  checked={slot.smtp_server_ids.length === 0}
                  onChange={() => onUpdate({ smtp_server_ids: [] })}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium">Alle verfügbaren SMTP-Server</div>
                  <div className="text-sm text-gray-500">Automatische Rotation über alle Server</div>
                </div>
              </label>
              
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  checked={slot.smtp_server_ids.length > 0}
                  onChange={() => {
                    if (slot.smtp_server_ids.length === 0 && smtpServers.length > 0) {
                      onUpdate({ smtp_server_ids: [smtpServers[0].id] });
                    }
                  }}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <div className="font-medium">Spezifische SMTP-Server</div>
                  <div className="text-sm text-gray-500">Auswahl bestimmter Server</div>
                </div>
              </label>
            </div>

            {/* SMTP-Server Liste */}
            {slot.smtp_server_ids.length > 0 && smtpServers.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {smtpServers.map((server) => (
                    <label key={server.id} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={slot.smtp_server_ids.includes(server.id)}
                        onChange={() => toggleSmtpServer(server.id)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{server.host}:{server.port}</div>
                        <div className="text-xs text-gray-500">{server.from_email}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  {slot.smtp_server_ids.length} von {smtpServers.length} Servern ausgewählt
                </div>
              </div>
            )}
          </div>

          {/* Anhänge */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 flex items-center">
              <Upload className="h-4 w-4 mr-2" />
              Anhänge
            </h4>

            {/* Globale Anhänge Info */}
            {globalAttachments.length > 0 && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-blue-900 mb-2">
                  Globale Anhänge (in allen Slots):
                </div>
                <div className="text-xs text-blue-700 space-y-1">
                  {globalAttachments.map((attachment, index) => (
                    <div key={index}>📎 {attachment.name || attachment}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Slot-spezifische Anhänge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Zusätzliche Anhänge (nur für diesen Slot)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <div className="text-center">
                  <div className="text-sm text-gray-500 mb-2">
                    {uploading ? 'Upload läuft...' : 'Slot-spezifische Anhänge hinzufügen'}
                  </div>
                  <input
                    type="file"
                    multiple
                    onChange={handleSlotAttachmentUpload}
                    className="text-sm text-gray-500"
                    disabled={uploading}
                  />
                </div>
                {slot.attachments && slot.attachments.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-gray-700 mb-2">Slot-Anhänge:</div>
                    <div className="space-y-1">
                      {slot.attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span>📎 {file.name || file} {file.size ? `(${Math.round(file.size/1024)}KB)` : ''}</span>
                          <button
                            onClick={() => {
                              const newAttachments = slot.attachments.filter((_, i) => i !== index);
                              onUpdate({ attachments: newAttachments });
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Erweiterte Optionen */}
          <div>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
            >
              {showAdvanced ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>Erweiterte Optionen</span>
            </button>

            {showAdvanced && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
                <div className="text-sm text-gray-600">
                  Weitere Konfigurationsoptionen können hier hinzugefügt werden.
                </div>
              </div>
            )}
          </div>

          {/* Validierungs-Warnungen */}
          {(!slot.subject.trim() || (slot.html_templates && !slot.html_templates[0]?.trim())) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-yellow-800">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">Unvollständige Konfiguration</span>
              </div>
              <div className="mt-2 text-sm text-yellow-700">
                {!slot.subject.trim() && <div>• Betreff fehlt</div>}
                {slot.html_templates && !slot.html_templates[0]?.trim() && <div>• HTML-Template fehlt</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignSlotEditor;
